import { ArrowRight } from 'lucide-react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useFlowGuidance } from '@/hooks/useFlowGuidance';
import { isFlowRoute } from '@/lib/flowGuidance';

// The bar's chrome, shared by the message and empty (space-reserving) states so the bar
// is exactly as tall either way, keeping the page below fixed whether or not it shows.
const BAR_CLASS = 'bg-primary/5 border-b border-border/40';
const ROW_CLASS =
	'w-full xl:container xl:mx-auto px-5 py-4 flex items-center gap-2 text-primary text-sm font-semibold';

/**
 * Canonical next-step line shown directly under the progress bar on every flow route.
 * It always states the single next action for the current step, giving a browsing
 * agent (and the visitor) one consistent place to read "what to do next" across the
 * whole flow. The cue is steady and high-contrast — no animation — so it reads clearly
 * from a single screenshot, the way a vision-based agent perceives the page.
 *
 * On a flow route the message is only momentarily absent (quotes still generating or
 * recalculating on the offer page). Rather than collapse — which would shift the whole
 * page up and back for a jarring flicker — the same bar renders with empty, height-
 * reserving contents so the page stays put. Off a flow route entirely there is no bar to
 * reserve, so it renders nothing. Driven by the same `useFlowGuidance` source as the
 * inline control highlights, so the two can never disagree.
 */
const FlowGuidanceBar = (): React.JSX.Element | null => {
	const { t } = useTranslation();
	const guidance = useFlowGuidance();
	const { pathname } = useLocation();

	// Off a flow route entirely there is no bar to show or reserve.
	if (!guidance && !isFlowRoute(pathname)) {
		return null;
	}

	// One bar, same chrome whether or not the message is showing — only the contents
	// switch. When guidance is momentarily absent (quotes generating/recalculating on the
	// offer page) the row still occupies its full height via an empty text-line box
	// (h-5 == text-sm's line-height), so the page below stays put instead of jumping.
	return (
		<div
			data-testid="flow-guidance-bar"
			role={guidance ? 'status' : undefined}
			aria-hidden={guidance ? undefined : true}
			className={BAR_CLASS}
		>
			<div className={ROW_CLASS}>
				{guidance ? (
					<>
						<ArrowRight className="w-4 h-4 shrink-0" aria-hidden="true" />
						<span>{t(guidance.messageKey)}</span>
					</>
				) : (
					<span className="block h-5" />
				)}
			</div>
		</div>
	);
};

export default FlowGuidanceBar;
